import * as React from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Typography,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { ChevronRight } from '@bb-ui/icons/dist/small/ChevronRight';
import { HelpCircle, Lock } from '@bb-ui/icons/dist/small';
import { LearnInstance } from '@bb-learn-instances-service/common';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DatabaseLookup, getSiteDatabase } from './learnSiteFilters';
import { useHasBeenOnScreen, useSiteLocked } from './hooks/useSiteLocked';

const LooseIconButton = IconButton as any;

const useStyles = makeStyles((theme: Theme) => ({
  statusLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.primary.dark,
    },
  },
  lockCell: {
    paddingLeft: theme.spacing(1),
    paddingRight: 0,
  },
  lockIcon: {
    verticalAlign: 'middle',
    color: theme.palette.text.secondary,
  },
}));

export interface LearnSiteRowProps {
  site: LearnInstance;
  index: number;
  rowClass?: string;
  cellTextClass?: string;
  databases?: DatabaseLookup;
  /** Show the Database column (the Database filter is on) */
  showDatabase?: boolean;
  /** Show the lock column and read the site's lock state once on screen (the Locked filter is on) */
  showLock?: boolean;
}

const NO_DATABASES: DatabaseLookup = { sharedDatabases: {}, loaded: false };

// `created` is epoch seconds; shown in UTC as on the Captain Learn list
const formatCreated = (created?: number) =>
  created ? `${new Date(created * 1000).toISOString().slice(0, 19).replace('T', ' ')} UTC` : '-';

export const LearnSiteRow: React.FunctionComponent<LearnSiteRowProps> = ({
  site,
  index,
  rowClass,
  cellTextClass,
  databases = NO_DATABASES,
  showDatabase = false,
  showLock = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const siteDatabase = getSiteDatabase(site, databases);
  let database = '-';
  if (siteDatabase.name) {
    database = siteDatabase.name;
  } else if (siteDatabase.type !== 'unknown') {
    database = t(`learnSites.databaseTypes.${siteDatabase.type}`);
  }
  const created = formatCreated(Number(site.created));
  const rowRef = React.useRef<HTMLTableRowElement>(null);
  const onScreen = useHasBeenOnScreen(rowRef);
  const locked = useSiteLocked(site.TenantId, showLock && onScreen);
  // Column numbers shift with the optional Lock and Database columns
  let column = 0;
  const nextColumn = () => {
    column += 1;
    return column;
  };

  return (
    <TableRow
      ref={rowRef}
      aria-rowindex={index + 1}
      data-testid={`site-list-table-row-${index}`}
      className={rowClass}
    >
      {showLock && (
        <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={classes.lockCell}>
          {locked === true && (
            <Tooltip title={t('learnSites.locked')}>
              <span
                role="img"
                aria-label={t('learnSites.locked')}
                data-testid={`site-locked-${index}`}
              >
                <Lock className={classes.lockIcon} />
              </span>
            </Tooltip>
          )}
          {locked === null && (
            <Tooltip title={t('learnSites.lockUnknown')}>
              <span
                role="img"
                aria-label={t('learnSites.lockUnknown')}
                data-testid={`site-lock-unknown-${index}`}
              >
                <HelpCircle className={classes.lockIcon} />
              </span>
            </Tooltip>
          )}
        </TableCell>
      )}
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
        <Tooltip title={site.Name || '-'}>
          <span>{site.Name || '-'}</span>
        </Tooltip>
      </TableCell>
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
        <Tooltip title={site.ClientName || '-'}>
          <span>{site.ClientName || '-'}</span>
        </Tooltip>
      </TableCell>
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
        <Tooltip title={site.InstallationId}>
          <span>{site.InstallationId}</span>
        </Tooltip>
      </TableCell>
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
        <Tooltip title={site.Status}>
          <span>{site.Status}</span>
        </Tooltip>
      </TableCell>
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
        <Tooltip title={site.FleetId || '-'}>
          <span>{site.FleetId || '-'}</span>
        </Tooltip>
      </TableCell>
      {showDatabase && (
        <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
          <Tooltip title={database}>
            <span data-testid={`site-database-${index}`}>{database}</span>
          </Tooltip>
        </TableCell>
      )}
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
        <Tooltip title={created}>
          <span>{created}</span>
        </Tooltip>
      </TableCell>
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} className={cellTextClass}>
        {site.Url ? (
          <a
            href={/^https?:\/\//i.test(site.Url) ? site.Url : `https://${site.Url}`}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.statusLink}
            onClick={(e) => e.stopPropagation()}
          >
            {site.Url}
          </a>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell aria-colindex={nextColumn()} tabIndex={-1} align="right">
        <LooseIconButton
          aria-label="View more information"
          component={Link}
          to={`/learn-site/${site.LearnSiteTenantId}/information`}
        >
          <Typography component="span" variant="inherit">
            {t('global.view')}
          </Typography>
          <ChevronRight />
        </LooseIconButton>
      </TableCell>
    </TableRow>
  );
};

export default LearnSiteRow;
